// ============================================================
// InkOverlay -- blooper ink splat effect rendered on a 2D canvas
// ============================================================
//
// When the player is hit by a Blooper item, dark ink blobs are
// drawn over the screen and fade out as the effect timer expires.
// ============================================================

/** Fixed blob layout so splats don't jump between frames. */
interface InkBlob {
  /** Normalised X position (0-1, relative to canvas width). */
  nx: number;
  /** Normalised Y position (0-1, relative to canvas height). */
  ny: number;
  /** Base radius in pixels (scaled by canvas dimensions). */
  radiusFactor: number;
}

export class InkOverlay {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private blobs: InkBlob[];

  /** The maximum blooper duration used to normalise alpha (seconds). */
  private static MAX_DURATION = 5;

  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.style.position = 'absolute';
    this.canvas.style.top = '0';
    this.canvas.style.left = '0';
    this.canvas.style.width = '100%';
    this.canvas.style.height = '100%';
    this.canvas.style.pointerEvents = 'none';
    this.canvas.style.zIndex = '6';

    // Match the device pixel dimensions
    this.resize();
    window.addEventListener('resize', this.handleResize);

    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('InkOverlay: failed to get 2d context');
    this.ctx = ctx;

    // Fixed blob positions (seeded pattern) -- 6 blobs
    this.blobs = [
      { nx: 0.20, ny: 0.25, radiusFactor: 0.14 },
      { nx: 0.70, ny: 0.18, radiusFactor: 0.12 },
      { nx: 0.45, ny: 0.55, radiusFactor: 0.16 },
      { nx: 0.80, ny: 0.65, radiusFactor: 0.11 },
      { nx: 0.15, ny: 0.72, radiusFactor: 0.13 },
      { nx: 0.55, ny: 0.85, radiusFactor: 0.10 },
    ];

    // Append to the game container
    const app = document.getElementById('game-canvas') ?? document.body;
    app.appendChild(this.canvas);
  }

  // ────────────────────────────────────────────────────────────
  // Per-frame update
  // ────────────────────────────────────────────────────────────

  /**
   * Call every frame.
   * @param blooperTimer Remaining blooper effect time in seconds (0 = inactive).
   * @param _dt Delta time in seconds (reserved for future animation use).
   */
  update(blooperTimer: number, _dt: number): void {
    const { ctx, canvas } = this;
    const w = canvas.width;
    const h = canvas.height;

    // Always clear first
    ctx.clearRect(0, 0, w, h);

    if (blooperTimer <= 0) return;

    // Alpha fades as timer decreases
    const alpha = Math.min(blooperTimer / InkOverlay.MAX_DURATION, 1) * 0.85;

    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = 'rgb(30, 0, 50)'; // dark purple-black

    const minDim = Math.min(w, h);

    for (const blob of this.blobs) {
      const x = blob.nx * w;
      const y = blob.ny * h;
      const radius = blob.radiusFactor * minDim;

      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  // ────────────────────────────────────────────────────────────
  // Cleanup
  // ────────────────────────────────────────────────────────────

  dispose(): void {
    window.removeEventListener('resize', this.handleResize);
    this.canvas.remove();
  }

  // ────────────────────────────────────────────────────────────
  // Internal helpers
  // ────────────────────────────────────────────────────────────

  private resize(): void {
    this.canvas.width = window.innerWidth * (window.devicePixelRatio || 1);
    this.canvas.height = window.innerHeight * (window.devicePixelRatio || 1);
  }

  private handleResize = (): void => {
    this.resize();
  };
}
